<template>
  <div
    class="rounded-md bg-white text-gray-750 border border-gray-300 shadow-sm transition-opacity opacity-0 pointer-events-none flex flex-nowrap"
    :class="{
      'opacity-100 pointer-events-auto': showing,
      'range-slider': !fullWidth,
      'range-slider-full-width': fullWidth,
      dark,
      vertical,
    }"
    @click.prevent="$emit('click', $event)"
  >
    <input
      class="transparent cursor-pointer appearance-none rounded-lg border-transparent h-2"
      :class="{ dark, 'w-full': fullWidth }"
      type="range"
      :value="numValue"
      :max="max"
      :min="min"
      :step="step"
      :style="{ '--slider-value': `${(numValue / max) * 100}%` }"
      @input="$emit('input', +($event.target as HTMLInputElement).value || 0)"
      @click.stop=""
    />
    <label v-if="showValueLabel" class="m-0 pl-2">{{ textValue }}</label>
  </div>
</template>

<script lang="ts" setup>
const props = defineProps({
  showing: {
    type: Boolean,
    default: true,
  },
  value: {
    type: [Number, Symbol],
    required: true,
  },
  vertical: {
    type: [Boolean, String] as PropType<boolean | 'vertical'>,
    default: false,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  dark: {
    type: [Boolean, String] as PropType<boolean | 'dark'>,
    default: false,
  },
  showValueLabel: {
    type: Boolean,
    default: true,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['input', 'click'])
const textValue = computed(() => (typeof props.value === 'number' ? `${props.value}` : '\u00a0\u2014\u00a0'))
const numValue = computed(() => (typeof props.value === 'number' ? props.value : 0))
</script>

<style lang="stylus" scoped>
input[type=range]
  @apply appearance-none bg-transparent h-1 /* Hides the slider so that custom slider can be made */

  // creates the illusion of the filled / empty sections of the slider being a different color
  background linear-gradient(90deg, theme('colors.copper.700') var(--slider-value), theme('colors.gray.200') calc(var(--slider-value) + 0.1%))

  &:focus
    outline none

  track()
    @apply w-full h-1 shadow-sm rounded-lg

  thumb()
    height 11px
    width 11px
    @apply rounded-lg bg-copper-700 border-0

  &::-webkit-slider-runnable-track
    track()

  &::-moz-range-track
    track()

  &::-webkit-slider-thumb
    thumb()
    @apply appearance-none
    margin-top -3px /* Chrome-only positioning */

  /* All the same stuff for Firefox */
  &::-moz-range-thumb
    thumb()

  &.dark
    background linear-gradient(90deg, theme('colors.copper.200') var(--slider-value), theme('colors.gray.700') calc(var(--slider-value) + 0.1%))
    @apply bg-copper-200
    &::-webkit-slider-thumb
      @apply bg-copper-200
    &::-moz-range-thumb
      @apply bg-copper-200


.range-slider
  @apply px-2 py-1 items-center
  label
    @apply w-10 h-auto
  input[type=range]
    @apply w-40
  &.vertical
    @apply p-1 items-center
    label
      @apply w-auto h-9
      writing-mode vertical-lr
    input[type=range]
      @apply w-28

  &.dark
    @apply bg-gray-900 text-white border-0


.range-slider-full-width
  @apply px-0 flex flex-1 py-1 items-center
  label
    @apply w-10 h-auto
  input[type=range]
    @apply w-full

  &.dark
    @apply bg-gray-900 text-white border-0
</style>
